"use client";

import type React from "react";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface FAQItemProps {
  item: { question: string; answer: string };
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ item, isOpen, onClick }) => {
  const words = item.answer.split(" ");

  return (
    <div className="border-b border-border py-6">
      <header
        className="flex justify-between items-center cursor-pointer select-none"
        onClick={onClick}
        role="button"
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-medium text-text">
          {item.question}
        </h3>
        <ChevronDown
          className={cn(
            "h-5 w-5 text-text transition-transform duration-300 shrink-0",
            isOpen && "rotate-180"
          )}
        />
      </header>
      <div
        className={cn(
          "grid overflow-hidden transition-all duration-300 ease-in-out",
          isOpen ? "grid-rows-[1fr] pt-4" : "grid-rows-[0fr]"
        )}
      >
        <div className="min-h-0">
          <div className="text-base text-beige">
            {isOpen &&
              words.map((word, i) => (
                <span
                  key={i}
                  className="inline-block opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  {word}&nbsp;
                </span>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function FAQs({ title, questions }: { title: string, questions: { question: string, answer: string }[] }) {
  const [openIndices, setOpenIndices] = useState<number[]>([]);

  const toggleFAQ = (index: number) => {
    setOpenIndices((prevIndices) =>
      prevIndices.includes(index)
        ? prevIndices.filter((i) => i !== index)
        : [...prevIndices, index]
    );
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-8">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-text">
          {title}
      </h2>
      <div className="space-y-2">
        {questions.map((item, index) => (
          <FAQItem
            key={`faq-v3-${index}`}
            item={item}
            isOpen={openIndices.includes(index)}
            onClick={() => toggleFAQ(index)}
          />
        ))}
      </div>
    </div>
  );
}
